import {FC, useEffect} from 'react';
import {RecoilState, useRecoilValue} from 'recoil';
import {useForm} from 'react-hook-form';

import {Box, Button, Divider, Modal, Stack, Toolbar, Typography} from '@mui/material';

import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import {FilterItem} from './FilterItem';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';

import {FilterType, IFiltersSet, IFiltersSetPostBody} from '../../../shared/types/filtersSet.types';

import {areFiltersArraysEqual} from '../../../shared/helpers/areFilttersArraysEqual';

import {green} from '@mui/material/colors';
import {ISelectOption} from '../../../api/strategies/option.type';
import {IStatusCodeTranslation, statusCodeTranslation} from '../../../shared/status.data';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  width: 500,
  p: 2,
};

type SaveSetModalType = {
  loading: boolean;
  open: boolean;
  handleClose: (innerEffect: boolean) => void;
  filters: FilterType[];
  currentFiltersSetState: RecoilState<IFiltersSet | null>;
  filtersState: RecoilState<FilterType[]>;
} & (
  | {
      innerEffect: false;
      handleSideEffect: () => (effect: 'edit' | 'save', set: IFiltersSet | IFiltersSetPostBody) => void;
      set: IFiltersSet | null;
    }
  | {
      innerEffect: true;
      handleSideEffect: () => (set: IFiltersSet) => void;
      set: IFiltersSet;
    }
);

export const SaveSetModal: FC<SaveSetModalType> = ({
  loading,
  open,
  handleClose,
  handleSideEffect,
  set,
  filters,
  innerEffect,
  currentFiltersSetState,
  filtersState,
}) => {
  const currentFiltersSet = useRecoilValue(currentFiltersSetState);
  const {
    control,
    handleSubmit,
    formState: {isDirty},
    setValue,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: set?.name,
      filters: set?.filters || [],
    },
  });

  const filtersToEditWatch = watch('filters');
  const nameWatch = !!set && watch('name');

  const onSubmitInner = async (data: {name?: string; filters?: FilterType[]}) => {
    if (!innerEffect) return;

    handleSideEffect()({
      ...set,
      name: data.name as string,
      filters: data.filters as FilterType[],
    });
  };

  const onSubmit = (effect: 'edit' | 'save') => async (data: {name?: string}) => {
    if (innerEffect) return;

    handleSideEffect()(
      effect,
      effect === 'edit' && set
        ? ({
            ...set,
            name: data.name as string,
            filters,
          } as IFiltersSet)
        : ({
            name: data.name as string,
            filters,
          } as IFiltersSet),
    );
  };

  console.log({innerEffect, filtersToEditWatch, filters});

  const handleEffectStrategy = () => {
    return innerEffect ? onSubmitInner : onSubmit(set ? 'edit' : 'save');
  };

  const handleDeleteFilter = (filterBy: string) => {
    setValue(
      'filters',
      filtersToEditWatch.filter((i) => i[0] !== filterBy),
      {shouldDirty: true},
    );
  };

  useEffect(() => {
    if (!innerEffect && set && !areFiltersArraysEqual(set.filters, filters)) {
      setValue('filters', filters, {shouldDirty: true});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal open={open}>
      <Box sx={style}>
        <Toolbar
          sx={{
            pl: {sm: 1},
            pr: {xs: 1, sm: 1},
            minHeight: '30px !important',
          }}
        >
          <Typography sx={{flex: '1 1 100%'}} variant="h6" id="formTitle" component="div">
            Сохранение фильтра
          </Typography>
        </Toolbar>
        <Divider sx={{m: '10px 0 20px'}} />
        <Stack direction="column" spacing={3}>
          <ControlledInput
            name="name"
            control={control}
            rules={{
              required: 'Обязательно для заполнения',
            }}
            size="small"
            label="Имя набора фильтров"
            disabled={loading}
            fullWidth
          />
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{
              '& > div': {
                margin: '5px',
              },
            }}
          >
            {innerEffect
              ? filtersToEditWatch.map((filter) => (
                  <FilterItem
                    key={`${filter[0]}:${filter[1]}`}
                    filterBy={filter[0]}
                    deletable={filtersToEditWatch.length > 1}
                    handleDelete={handleDeleteFilter}
                    value={typeof filter[1] === 'object' ? (filter[1] as ISelectOption).label : filter[1]}
                    filtersState={filtersState}
                    labelAsValue={
                      statusCodeTranslation.serviceRequest[filter[1] as keyof IStatusCodeTranslation['serviceRequest']]
                    }
                  />
                ))
              : filters.map((filter) => (
                  <FilterItem
                    key={`${filter[0]}:${typeof filter[1] === 'object' ? (filter[1] as ISelectOption).label : filter[1]}`}
                    filterBy={filter[0]}
                    value={typeof filter[1] === 'object' ? (filter[1] as ISelectOption).label : filter[1]}
                    filtersState={filtersState}
                    labelAsValue={
                      statusCodeTranslation.serviceRequest[filter[1] as keyof IStatusCodeTranslation['serviceRequest']]
                    }
                  />
                ))}
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Button variant="outlined" color="error" disabled={loading} onClick={() => handleClose(innerEffect)}>
              Отменить
            </Button>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="success"
                onClick={handleSubmit(handleEffectStrategy())}
                disabled={loading || (!innerEffect && !isDirty) || (innerEffect && !isDirty)}
              >
                Сохранить
              </Button>
              {set && !innerEffect && (
                <LoadingButton
                  variant="contained"
                  color="success"
                  disabled={!isDirty || nameWatch === currentFiltersSet?.name}
                  onClick={handleSubmit(onSubmit('save'))}
                  loading={loading}
                  sx={{
                    backgroundColor: green[500],
                    '&:hover': {
                      backgroundColor: green[600],
                    },
                  }}
                >
                  Создать новый
                </LoadingButton>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
